<template>
  <SmDialog
    v-model:visibility="visible"
    :size="dialogSize"
    :title="drawerTitle">
    <SmSteps
      v-model:active-step-key="activeStepKey"
      content-height="calc(70vh - 4rem)"
      :steps="steps">
      <!-- User Form Content -->
      <template #step-person-form>
        <PeopleForm
          ref="peopleForm"
          v-model:person="person"
          :extra-items="[{ name: 'tags', required: false, label: 'Tags' }]">
          <template #extra-item-tags>
            <TagSelect
              :selected="person.tags"
              :available-tags="allTags"
              @add-tag="addTagToPerson"
              @delete-tag="removeTagFromPerson">
              <div class="align wrap cursor-pointer flex-row gap-1">
                <span v-for="tagId in person.tags" :key="tagId">
                  <TagComponent :tag="getTag(tagId) as Tag" />
                </span>
                <Plus :size="18" />
              </div>
            </TagSelect>
          </template>
        </PeopleForm>
      </template>
      <!-- License Selection Content -->
      <template #step-license-select>
        <GroupAddSoftware
          v-model:selection="licenseSelection"
          :all-contracts
          :all-software-licenses />
      </template>
      <!-- Overview Content for selected Licenses and User -->
      <template #step-overview>
        <div class="person-overview flex-col gap-1">
          <div class="align flex-row gap-1">
            <SmAvatar
              class="bg-gray-200"
              :name="
                getPersonNameDisplay(person.first_name, person.last_name)
              " />
            <div class="flex-col">
              <h3>
                {{ getPersonNameDisplay(person.first_name, person.last_name) }}
              </h3>
              <p>
                {{ person.email }}
              </p>
            </div>
          </div>
          <h3>{{ i18n.t('informations') }}</h3>
          <SmDescriptions
            :descriptions="[
              {
                key: 'firstName',
                title: i18n.t('general.firstName'),
                content: person.first_name || undefined,
              },
              {
                key: 'lastName',
                title: i18n.t('general.lastName'),
                content: person.last_name || undefined,
              },
              {
                key: 'phone',
                title: i18n.t('phone'),
                content: person.phone || undefined,
              },
              {
                key: 'employeeId',
                title: i18n.t('general.employeeId'),
                content: person.employee_id || undefined,
              },
            ]">
          </SmDescriptions>
          <SmDescriptions
            :number-columns="1"
            :descriptions="[{ key: 'tags', title: 'Tags' }]">
            <template #field-tags>
              <span v-for="tagId in person.tags" :key="tagId">
                <TagComponent :tag="getTag(tagId) as Tag" />
              </span>
            </template>
          </SmDescriptions>
          <h3 v-if="!isEmpty(licenseSelection)">{{ i18n.t('licenses') }}</h3>
          <div v-if="!isEmpty(licenseSelection)" class="flex-col gap-2">
            <span v-for="(app, appId) of licenseSelection" :key="appId">
              <span v-for="(models, subAccountId) in app" :key="subAccountId">
                <div
                  v-if="hasEntries(models)"
                  class="align pad-b-1 flex-row gap-1">
                  <ApplicationIcon
                    v-if="getApp(appId)"
                    :size="30"
                    :application-name="getApp(appId)?.software_name ?? ''"
                    :application-type="getApp(appId)?.type" />
                  <h4>
                    {{ getSoftwareDisplayName(getApp(appId)) }}
                  </h4>
                  <span
                    v-if="getSubAcc(appId, subAccountId)"
                    class="subaccount-text">
                    {{ getSubAcc(appId, subAccountId)!.name }}
                  </span>
                </div>
                <LicenseModelEditTable
                  v-if="hasEntries(models)"
                  :sub-account-id="subAccountId.toString()"
                  :selectable="false"
                  :contracts="allContracts"
                  :software-licenses="filterSoftwareLicenses(appId.toString())"
                  :application="getApp(appId) as SoftwareOut" />
              </span>
            </span>
          </div>
        </div>
      </template>
      <template #step-overview-next-button="{ goToNextStep }">
        <SmButton @click="submitAllRequests(goToNextStep)">
          {{ i18n.t('createUser') }}
        </SmButton>
      </template>
      <!-- Request Confirmation Content for person and license requests -->
      <template #step-request-confirmation>
        <div class="pad-b-1 flex-col gap-2">
          <span v-for="(feedbackItem, index) in requests" :key="index">
            <RequestFeedbackDisplay
              v-if="feedbackItem.type == 'person-post'"
              v-model:status="feedbackItem.status"
              :success-message="
                i18n.t('accountCreatedSuccessfully', { email: person.email })
              "
              :request="feedbackItem.request">
              <SmAvatar
                size="small"
                class="bg-gray-200"
                :name="
                  getPersonNameDisplay(person.first_name, person.last_name)
                " />
              <div class="flex-col">
                <h3>
                  {{
                    getPersonNameDisplay(person.first_name, person.last_name)
                  }}
                </h3>
                <span>
                  {{ i18n.t('creatingAnAccountFor', { email: person.email }) }}
                </span>
              </div>
            </RequestFeedbackDisplay>
            <RequestFeedbackDisplay
              v-else-if="
                requests &&
                feedbackItem.type == 'license-post' &&
                requests[index - 1].status == 'finished'
              "
              v-model:status="feedbackItem.status"
              :success-message="
                i18n.t('licenseCreatedSuccessfully', {
                  email: feedbackItem.resource.email,
                  softwareName: getApp(feedbackItem.resource.software_id)!
                    .software_name,
                })
              "
              :request="feedbackItem.request">
              <ApplicationIcon
                :size="40"
                :application-name="
                  getApp(feedbackItem.resource.software_id)!.software_name
                "
                :application-type="
                  getApp(feedbackItem.resource.software_id)!.type
                " />
              <div class="flex-col">
                <div class="align flex-row gap-1">
                  <h3>
                    {{
                      getSoftwareDisplayName(
                        getApp(feedbackItem.resource.software_id)!
                      )
                    }}
                  </h3>
                  <span class="subaccount-text">
                    {{
                      getSubAcc(
                        feedbackItem.resource.software_id,
                        feedbackItem.resource.sub_account_id
                      )!.name
                    }}
                  </span>
                </div>
                <span>
                  {{
                    i18n.t('creatingAnAccountFor', {
                      email: feedbackItem.resource.email,
                    })
                  }}
                </span>
              </div>
            </RequestFeedbackDisplay>
            <div
              v-if="
                requests &&
                index < requests.length - 1 &&
                requests[index].status == 'finished'
              "
              class="divider" />
          </span>
        </div>
      </template>
      <template #step-request-confirmation-back-button>
        <!-- The span is necessary to hide the back-button -->
        <span></span>
      </template>
      <template #finish-button>
        <SmButton @click="handleClose"> {{ i18n.t('close') }} </SmButton>
      </template>
    </SmSteps>
  </SmDialog>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    AccountIn,
    CancelablePromise,
    LicencePostIn,
    PriceInformation,
    SoftwareLicense,
    SoftwareLicenseMini_Input,
    SoftwareOut,
    Tag,
    UserLicencesService,
    UsersService,
  } from '@/client'
  import { Plus } from 'lucide-vue-next'
  import PeopleForm from './PeopleForm.vue'
  import { SelectionState } from '@/components/groups/GroupAddSoftware.vue'
  import { PersonStore } from '@/stores/person/person.store'

  // Utility-type for below, Conditionally resolving to return-type of UserPost or LicensePost request
  type RequestReturn<T extends 'license-post' | 'person-post'> =
    T extends 'person-post'
      ? ReturnType<(typeof UsersService)['addUserApiV1ManagementUsersPost']>
      : ReturnType<
          (typeof UserLicencesService)['postLicenceApiV1SoftwareLicencesPost']
        >

  type RequestFeedbackInfo<T extends 'license-post' | 'person-post'> = {
    type: T
    request: () => RequestReturn<T>
    resource: T extends 'license-post' ? LicencePostIn : AccountIn
    status: 'not-started' | 'loading' | 'finished'
  }

  const props = defineProps<{
    allApps: SoftwareOut[]
    allSoftwareLicenses: SoftwareLicense[]
    allContracts: PriceInformation[]
    allTags: Tag[]
  }>()

  const i18n = useI18n()

  const person = ref<AccountIn & { tags: string[] }>({
    email: '',
    tags: [],
  })

  const peopleForm = ref<InstanceType<typeof PeopleForm>>()
  const licenseSelection = ref<SelectionState>({})
  // all selected License-Models by appId (better should be by appId & subAccountId)
  // First entry of requests is the person-post-request, following entries must be license-post-requests
  const requests =
    ref<
      [
        RequestFeedbackInfo<'person-post'>,
        ...RequestFeedbackInfo<'license-post'>[],
      ]
    >()
  const steps = ref([
    {
      key: 'person-form',
      nextText: i18n.t('chooseLicenses'),
      // Validation of the form is required to enter next step
      nextStepValidator: () => {
        return (peopleForm.value?.validate() as Promise<boolean>) || false
      },
    },
    {
      key: 'license-select',
      nextText: i18n.t('overview'),
      backText: i18n.t('editUser'),
    },
    {
      key: 'overview',
      backText: i18n.t('editLicenses'),
    },
    {
      key: 'request-confirmation',
      backText: i18n.t('overview'),
    },
  ])
  const activeStepKey = ref('person-form') // starting with person-form
  const visible = defineModel<boolean>('visible', { default: false })

  // dialog will be bigger for license-selection
  const dialogSize = computed(() => {
    switch (activeStepKey.value) {
      case 'license-select':
        return 'large' as const
      default:
        return 'medium' as const
    }
  })

  const drawerTitle = computed(() => {
    switch (activeStepKey.value) {
      case 'person-form':
        return i18n.t('createUser')
      case 'license-select':
        return i18n.t('addLicenses')
      case 'overview':
        return i18n.t('overview')
      case 'request-confirmation':
        return i18n.t('confirmation')
      default:
        return i18n.t('createUser')
    }
  })

  function filterSoftwareLicenses(appId: string) {
    return props.allSoftwareLicenses.filter((s) => s.software_id == appId)
  }

  function isEmpty(state: SelectionState) {
    let isEmpty = true
    for (const app of Object.values(state)) {
      if (app == null) continue
      for (const subAccEntry of Object.values(app)) {
        for (const model of Object.values(subAccEntry)) {
          if (model != null) {
            isEmpty = false
            break
          }
        }
      }
    }
    return isEmpty
  }

  function hasEntries<T>(record: Record<string, T>) {
    return Object.keys(removeNull(record)).length > 0
  }

  function removeNull<T>(record: Record<string, T | null>) {
    const returnRecord: Record<string, T> = {}
    for (const [id, entry] of Object.entries(record)) {
      if (entry != null) returnRecord[id] = entry
    }
    return returnRecord
  }

  function addTagToPerson(tagId: string) {
    person.value.tags.push(tagId)
  }
  function removeTagFromPerson(tagId: string) {
    person.value.tags = person.value.tags.filter((t) => t != tagId)
  }

  // Mapping DB Models to Mini_Input for license-post-requests
  function mapLicenseModels(
    models: Record<string, SoftwareLicense | null>
  ): Record<string, SoftwareLicenseMini_Input> {
    const returnModels: Record<string, SoftwareLicenseMini_Input> = {}
    const mappedModels = Object.values(models).map((m) => {
      if (m == null) return null
      return {
        // todo: should be switched to '_id' when implemented
        name: m.name,
      }
    })
    Object.keys(models).forEach((key, index) => {
      if (mappedModels[index] == null) return
      returnModels[key] = mappedModels[index]!
    })
    return returnModels
  }

  function getPersonNameDisplay(
    first_name: string | undefined | null,
    last_name: string | undefined | null
  ) {
    let name = ''
    if (first_name) name += first_name + ' '
    if (last_name) name += last_name
    if (!first_name && !last_name) name = i18n.t('noNameSpecified')
    return name
  }

  function getSoftwareDisplayName(a?: SoftwareOut) {
    if (!a) return i18n.t('noNameSpecified')
    return a.display_name || a.software_name
  }

  function getTag(tagId: string) {
    return props.allTags.find((t) => t._id == tagId)
  }
  function getSubAcc(appId: string | number, id: string | number) {
    return getApp(appId)?.sub_accounts[id]
  }
  function getApp(id: string | number) {
    return props.allApps.find((a) => a._id == id)
  }

  // Making person-post request and all license-post requests
  function submitAllRequests(goToNextStep: () => unknown) {
    const personPostRequest = () =>
      new Promise<Account>((resolve, reject) => {
        PersonStore.createPerson(person.value).then((res) => {
          if (res.error || !res.data) return reject(res.error)
          resolve(res.data)
        })
      }) as CancelablePromise<Account>

    const personRequestInfo: RequestFeedbackInfo<'person-post'> = {
      type: 'person-post',
      request: personPostRequest,
      resource: person.value,
      status: 'not-started',
    }
    requests.value = [personRequestInfo]

    for (const [softwareId, softwareSelect] of Object.entries(
      licenseSelection.value
    )) {
      if (softwareSelect == null) continue
      for (const [subAccountId, subAccountSelect] of Object.entries(
        softwareSelect
      )) {
        if (!hasEntries(subAccountSelect)) continue
        const licence: LicencePostIn = {
          email: person.value.email,
          sub_account_id: subAccountId,
          software_id: softwareId,
          software_licenses: mapLicenseModels(subAccountSelect),
        }
        const licenseRequest = () => {
          return UserLicencesService.postLicenceApiV1SoftwareLicencesPost({
            requestBody: licence,
          })
        }
        const licenseRequestInfo: RequestFeedbackInfo<'license-post'> = {
          type: 'license-post',
          request: licenseRequest,
          resource: licence,
          status: 'not-started',
        }
        requests.value!.push(licenseRequestInfo)
      }
    }

    goToNextStep()
  }

  function handleClose() {
    visible.value = false
    activeStepKey.value = 'person-form'
    person.value = { email: '', tags: [] }
    licenseSelection.value = {}
    requests.value = undefined
  }
</script>

<style scoped lang="scss">
  .flex-row {
    display: flex;
    flex-direction: row;
  }

  .align {
    align-items: center;
  }

  .flex-col {
    display: flex;
    flex-direction: column;
  }

  .gap-1 {
    gap: 1rem;
  }

  .gap-2 {
    gap: 2rem;
  }

  .pad-b-1 {
    padding-bottom: 1rem;
  }

  .wrap {
    flex-wrap: wrap;
  }

  .person-overview {
    padding-bottom: 2rem;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .subaccount-text {
    font-size: var(--font-size-small);
    margin-left: -0.5rem;
    color: var(--sm-contrast-muted);
  }

  .divider {
    margin-top: 2rem;
    margin-left: 5%;
    border-top: 1px solid var(--sm-contrast-muted);
    width: 90%;
    height: 1px;
  }
</style>
